import type { DataFetchParams } from '@/components/DataTable';
import {
  OrganizationScanAlertsSortField,
  ORGANIZATION_ALERTS_FILTERABLE_FIELDS,
} from '@kirin/api-client/contracts/alerts';

const USERS_FIELD = ORGANIZATION_ALERTS_FILTERABLE_FIELDS.USERS;
const ENDPOINTS_FIELD = ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINTS;
const ENDPOINT_COUNT_SORT_FIELD = OrganizationScanAlertsSortField.EndpointCount;

/**
 * Strip persisted filters/sort whose column is currently gated off so URL
 * state from a prior session can't bypass the gate (and the cache key stays
 * stable). When every gate is open the params pass through untouched.
 *
 * Two independent gates:
 * - `canFilterByUser` (KIR-3266, owner/manager only) — drops the `users`
 *   *filter*. The members-list endpoint that backs the Users filter options is
 *   RBAC-gated to owner/manager, so a stale `users` filter from a member's URL
 *   must never reach the server. Sort is left intact: a member keeps the
 *   sortable Users column (sort-by-userCount does not hit the members route).
 * - `endpointsColumnEnabled` (KIR-3035 `endpoint_identifier` flag) — drops the
 *   `endpoints` filter + `endpointCount` sort. With the flag off the column is
 *   hidden, so a stale URL must not still filter/sort by endpoint identity.
 *
 * Wired into the org alerts table's `queryFn` / `getQueryKeysFromParams`. Both
 * gates default to open so a caller that has neither — the user-dashboard
 * alerts table, which has no Users filter and no Endpoints column — would get
 * the params back untouched; it therefore skips this call entirely.
 */
export function sanitizeAlertsParams(
  params: DataFetchParams,
  canFilterByUser: boolean = true,
  endpointsColumnEnabled: boolean = true
): DataFetchParams {
  if (canFilterByUser && endpointsColumnEnabled) return params;
  const filters = params.filters.filter(
    f =>
      (canFilterByUser || f.field !== USERS_FIELD) &&
      (endpointsColumnEnabled || f.field !== ENDPOINTS_FIELD)
  );
  const droppedSort =
    !endpointsColumnEnabled && params.sort?.field === ENDPOINT_COUNT_SORT_FIELD;
  const sort = droppedSort ? undefined : params.sort;
  return { ...params, filters, sort };
}
